import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { scr } from '../../lib/api';
import type { TenderNotice } from '../../lib/types';
import { useApiQuery } from '../../lib/useApi';
import { ErrorNote, PageHeader, Spinner, StatusBadge, Tabs } from '../../ui/components';
import { AwardTab } from './tender/AwardTab';
import { BidTab } from './tender/BidTab';
import { EvaluationTab } from './tender/EvaluationTab';
import { OverviewTab } from './tender/OverviewTab';

const TABS = [
  { key: 'overview', label: 'Overview' },
  { key: 'bidding', label: 'Bidding' },
  { key: 'evaluation', label: 'Evaluation' },
  { key: 'award', label: 'Award & Contract' },
] as const;
type TabKey = (typeof TABS)[number]['key'];

export function TenderDetail() {
  const { id = '' } = useParams();
  const [tab, setTab] = useState<TabKey>('overview');
  const tenderQ = useApiQuery<TenderNotice>(`/api/v1/m11/tenders/${id}`);

  if (tenderQ.loading) return <Spinner label="Loading tender…" />;
  if (tenderQ.error || !tenderQ.data) {
    return (
      <div>
        <Link to="/procurement/tenders" className="text-sm font-semibold text-brand-600 hover:underline">← Back to tenders</Link>
        <div className="mt-4"><ErrorNote message={tenderQ.error ?? 'Tender not found'} /></div>
      </div>
    );
  }

  const tender = tenderQ.data;

  return (
    <div>
      <Link to="/procurement/tenders" className="mb-3 inline-block text-sm font-semibold text-brand-600 hover:underline">← Back to tenders</Link>
      <PageHeader
        title={tender.title}
        subtitle={`${tender.refNo} · ${tender.mdaCode} · ${tender.category} · ${tender.method}`}
        actions={<StatusBadge status={tender.status} />}
      />

      <div className="mb-5 grid gap-3 sm:grid-cols-4">
        <div className="card px-4 py-3">
          <div className="text-xs font-semibold uppercase text-slate-500">Estimated value</div>
          <div className="mt-0.5 text-sm font-bold tabular-nums">{scr(tender.estimatedValueScr)}</div>
        </div>
        <div className="card px-4 py-3">
          <div className="text-xs font-semibold uppercase text-slate-500">Document fee</div>
          <div className="mt-0.5 text-sm font-bold tabular-nums">{scr(tender.docFeeScr)}</div>
        </div>
        <div className="card px-4 py-3">
          <div className="text-xs font-semibold uppercase text-slate-500">Bid bond</div>
          <div className="mt-0.5 text-sm font-bold tabular-nums">{scr(tender.bidBondScr)}</div>
        </div>
        <div className="card px-4 py-3">
          <div className="text-xs font-semibold uppercase text-slate-500">Sealed-bid key</div>
          <div className="mt-0.5 text-sm font-bold">{tender.hasSealKey ? `Ready (${tender.quorumThreshold}-of-${tender.quorumTotal})` : 'Not minted yet'}</div>
        </div>
      </div>

      <Tabs tabs={TABS} value={tab} onChange={setTab} />

      {tab === 'overview' ? <OverviewTab tender={tender} onChanged={tenderQ.reload} /> : null}
      {tab === 'bidding' ? <BidTab tender={tender} onChanged={tenderQ.reload} /> : null}
      {tab === 'evaluation' ? <EvaluationTab tender={tender} onChanged={tenderQ.reload} /> : null}
      {tab === 'award' ? <AwardTab tender={tender} onChanged={tenderQ.reload} /> : null}
    </div>
  );
}
